export const advancedTopicsLessons = [
  {
    id: 'precalc-8',
    title: 'Law of Sines & Law of Cosines',
    unit: 'Trigonometry',
    description: 'Solve non-right triangles using Law of Sines and Law of Cosines, including the ambiguous case',
    standards: ['CCSS.MATH.CONTENT.HSG.SRT.D.10', 'NCTM: Geometry', 'Ohio: PC.T.3', 'NY: AI-G.SRT.10'],
    vocabulary: [
      { term: 'Law of Sines', definition: 'a/sin(A) = b/sin(B) = c/sin(C) for any triangle' },
      { term: 'Law of Cosines', definition: 'c² = a² + b² - 2ab·cos(C)' },
      { term: 'Ambiguous Case', definition: 'SSA condition that may yield 0, 1, or 2 triangles' },
      { term: 'Oblique Triangle', definition: 'A triangle with no right angle' },
      { term: 'SAS', definition: 'Side-Angle-Side: two sides and the included angle are known' },
      { term: 'SSS', definition: 'Side-Side-Side: all three sides are known' },
      { term: 'ASA', definition: 'Angle-Side-Angle: two angles and the included side are known' },
      { term: 'AAS', definition: 'Angle-Angle-Side: two angles and a non-included side are known' }
    ],
    concepts: [
      'Law of Sines: use when you have AAS, ASA, or SSA',
      'Law of Cosines: use when you have SAS or SSS',
      'SSA (ambiguous case) may produce 0, 1, or 2 valid triangles',
      'Always check if the sum of angles equals 180°',
      'Law of Cosines reduces to Pythagorean theorem when C = 90°',
      'Use Law of Sines to find angles after using Law of Cosines'
    ],
    examples: [
      {
        problem: 'Triangle ABC: a = 10, b = 15, C = 60°. Find side c.',
        steps: [
          'Use Law of Cosines: c² = a² + b² - 2ab·cos(C)',
          'c² = 10² + 15² - 2(10)(15)·cos(60°)',
          'c² = 100 + 225 - 300(0.5)',
          'c² = 325 - 150 = 175',
          'c = √175 ≈ 13.23'
        ]
      },
      {
        problem: 'Triangle ABC: A = 40°, B = 70°, a = 12. Find side b.',
        steps: [
          'Use Law of Sines: a/sin(A) = b/sin(B)',
          '12/sin(40°) = b/sin(70°)',
          'b = 12·sin(70°)/sin(40°)',
          'b ≈ 12(0.9397)/(0.6428)',
          'b ≈ 17.54'
        ]
      }
    ],
    teacherTip: 'For the ambiguous case (SSA), have students draw diagrams to visualize whether 0, 1, or 2 triangles are possible. Check if the given side opposite the angle is shorter than, equal to, or longer than the altitude.',
    questions: [
      {
        id: 'laws-1',
        question: 'Which law should you use for SAS?',
        options: ['Law of Sines', 'Law of Cosines', 'Pythagorean Theorem', 'Either A or B'],
        correctAnswer: 1,
        explanation: 'Law of Cosines is used when you have two sides and the included angle (SAS).'
      },
      {
        id: 'laws-2',
        question: 'Which law should you use for AAS?',
        options: ['Law of Sines', 'Law of Cosines', 'Pythagorean Theorem', 'Either A or B'],
        correctAnswer: 0,
        explanation: 'Law of Sines is used when you have two angles and a side (AAS or ASA).'
      },
      {
        id: 'laws-3',
        question: 'What is the Law of Sines formula?',
        options: ['a² + b² = c²', 'a/sin(A) = b/sin(B) = c/sin(C)', 'c² = a² + b² - 2ab·cos(C)', 'sin(A + B) = sin(A)cos(B) + cos(A)sin(B)'],
        correctAnswer: 1,
        explanation: 'Law of Sines: a/sin(A) = b/sin(B) = c/sin(C).'
      },
      {
        id: 'laws-4',
        question: 'What is the Law of Cosines formula?',
        options: ['a² + b² = c²', 'a/sin(A) = b/sin(B)', 'c² = a² + b² - 2ab·cos(C)', 'c = a·cos(B) + b·cos(A)'],
        correctAnswer: 2,
        explanation: 'Law of Cosines: c² = a² + b² - 2ab·cos(C).'
      },
      {
        id: 'laws-5',
        question: 'Which case is ambiguous?',
        options: ['SSS', 'SAS', 'SSA', 'ASA'],
        correctAnswer: 2,
        explanation: 'SSA (Side-Side-Angle) is the ambiguous case that may yield 0, 1, or 2 triangles.'
      },
      {
        id: 'laws-6',
        question: 'In triangle ABC, a = 8, b = 10, C = 90°. Find c using Law of Cosines.',
        options: ['c = 6', 'c = 12', 'c = 12.81', 'c = 18'],
        correctAnswer: 2,
        explanation: 'c² = 64 + 100 - 2(8)(10)(0) = 164, so c = √164 ≈ 12.81.'
      },
      {
        id: 'laws-7',
        question: 'In triangle ABC, A = 30°, a = 5, b = 10. How many triangles are possible?',
        options: ['0', '1', '2', 'Cannot determine'],
        correctAnswer: 0,
        explanation: 'Since a < b·sin(A) = 10·sin(30°) = 5, no triangle is possible (side a is too short).'
      },
      {
        id: 'laws-8',
        question: 'In triangle ABC, a = 7, b = 9, c = 12. Find angle C using Law of Cosines.',
        options: ['90°', '100.3°', '110.5°', '120°'],
        correctAnswer: 1,
        explanation: 'cos(C) = (49 + 81 - 144)/(2·7·9) = -14/126 ≈ -0.111, C ≈ 100.3°.'
      },
      {
        id: 'laws-9',
        question: 'In triangle ABC, A = 50°, B = 60°, a = 10. Find angle C.',
        options: ['60°', '70°', '80°', '90°'],
        correctAnswer: 1,
        explanation: 'C = 180° - A - B = 180° - 50° - 60° = 70°.'
      },
      {
        id: 'laws-10',
        question: 'Which condition guarantees exactly one triangle?',
        options: ['SSA with a < b', 'SSS', 'SSA with a = b·sin(A)', 'ASA'],
        correctAnswer: 3,
        explanation: 'ASA (and AAS, SAS, SSS) always produce exactly one unique triangle.'
      },
      {
        id: 'laws-11',
        question: 'In triangle ABC, a = 5, b = 7, C = 60°. Find c.',
        options: ['c ≈ 6.24', 'c ≈ 7.00', 'c ≈ 8.66', 'c ≈ 10.00'],
        correctAnswer: 0,
        explanation: 'c² = 25 + 49 - 2(5)(7)cos(60°) = 74 - 35 = 39, c ≈ 6.24.'
      },
      {
        id: 'laws-12',
        question: 'In triangle ABC, A = 45°, a = 10, b = 12. Is this the ambiguous case?',
        options: ['Yes', 'No', 'Cannot determine', 'Only if C > 90°'],
        correctAnswer: 0,
        explanation: 'SSA with angle A given is the ambiguous case. Need to check if 0, 1, or 2 triangles exist.'
      },
      {
        id: 'laws-13',
        question: 'In triangle ABC, a = 8, b = 6, c = 10. What type of triangle is it?',
        options: ['Acute', 'Right', 'Obtuse', 'Cannot determine'],
        correctAnswer: 1,
        explanation: 'Check: 10² = 8² + 6²? 100 = 64 + 36 ✓. Right triangle.'
      },
      {
        id: 'laws-14',
        question: 'In triangle ABC, A = 30°, B = 45°, c = 20. Find side a.',
        options: ['a ≈ 10.35', 'a ≈ 14.64', 'a ≈ 20.00', 'a ≈ 28.28'],
        correctAnswer: 0,
        explanation: 'C = 105°. a/sin(30°) = 20/sin(105°), a = 20·sin(30°)/sin(105°) ≈ 10.35.'
      },
      {
        id: 'laws-15',
        question: 'When does Law of Cosines reduce to Pythagorean theorem?',
        options: ['When A = 45°', 'When C = 60°', 'When C = 90°', 'Never'],
        correctAnswer: 2,
        explanation: 'When C = 90°, cos(90°) = 0, so c² = a² + b² - 0 = a² + b².'
      },
      {
        id: 'laws-16',
        question: 'In triangle ABC, a = 10, b = 10, C = 120°. Find c.',
        options: ['c ≈ 10', 'c ≈ 14.14', 'c ≈ 17.32', 'c ≈ 20'],
        correctAnswer: 2,
        explanation: 'c² = 100 + 100 - 2(10)(10)cos(120°) = 200 - 200(-0.5) = 300, c ≈ 17.32.'
      },
      {
        id: 'laws-17',
        question: 'In triangle ABC, a = 5, b = 12, c = 13. Find angle C.',
        options: ['30°', '60°', '90°', '120°'],
        correctAnswer: 2,
        explanation: '13² = 5² + 12²? 169 = 25 + 144 = 169 ✓. Right triangle, C = 90°.'
      },
      {
        id: 'laws-18',
        question: 'In SSA, if a > b, how many triangles are possible?',
        options: ['0', '1', '2', '1 or 2'],
        correctAnswer: 1,
        explanation: 'If the side opposite the given angle is longer than the other side, exactly 1 triangle exists.'
      },
      {
        id: 'laws-19',
        question: 'In triangle ABC, A = 40°, a = 6, b = 8. Find sin(B).',
        options: ['sin(B) ≈ 0.857', 'sin(B) ≈ 0.643', 'sin(B) ≈ 0.500', 'sin(B) ≈ 0.766'],
        correctAnswer: 0,
        explanation: '6/sin(40°) = 8/sin(B), sin(B) = 8·sin(40°)/6 ≈ 0.857.'
      },
      {
        id: 'laws-20',
        question: 'Which formula finds the area of a triangle given SAS?',
        options: ['Area = (1/2)bh', 'Area = (1/2)ab·sin(C)', 'Area = √[s(s-a)(s-b)(s-c)]', 'Both B and C'],
        correctAnswer: 1,
        explanation: 'For SAS, use Area = (1/2)ab·sin(C).'
      },
      {
        id: 'laws-21',
        question: 'In triangle ABC, a = 7, b = 9, C = 45°. Find the area.',
        options: ['22.27', '31.5', '44.55', '63'],
        correctAnswer: 0,
        explanation: 'Area = (1/2)(7)(9)sin(45°) = 31.5(√2/2) ≈ 22.27.'
      },
      {
        id: 'laws-22',
        question: 'In triangle ABC, all sides are known. Which law finds angle A?',
        options: ['Law of Sines only', 'Law of Cosines only', 'Either law', 'Neither law'],
        correctAnswer: 1,
        explanation: 'With SSS, use Law of Cosines first: cos(A) = (b² + c² - a²)/(2bc).'
      },
      {
        id: 'laws-23',
        question: 'In triangle ABC, A = 35°, a = 8, b = 10. Could there be 2 triangles?',
        options: ['Yes', 'No', 'Only if B < 90°', 'Only if B > 90°'],
        correctAnswer: 0,
        explanation: 'SSA with a < b and a > b·sin(A) may yield 2 triangles (ambiguous case).'
      },
      {
        id: 'laws-24',
        question: 'In triangle ABC, a = 15, b = 20, c = 25. What is angle C?',
        options: ['60°', '75°', '90°', '105°'],
        correctAnswer: 2,
        explanation: '25² = 15² + 20²? 625 = 225 + 400 = 625 ✓. Right triangle, C = 90°.'
      },
      {
        id: 'laws-25',
        question: 'In triangle ABC, A = 60°, B = 60°, a = 10. Find side b.',
        options: ['5', '10', '10√3', '20'],
        correctAnswer: 1,
        explanation: 'Since A = B, the triangle is isosceles with a = b = 10.'
      },
      {
        id: 'laws-26',
        question: 'In triangle ABC, a = 4, b = 5, c = 6. Is it acute, right, or obtuse?',
        options: ['Acute', 'Right', 'Obtuse', 'Cannot determine'],
        correctAnswer: 0,
        explanation: 'Check largest angle C: cos(C) = (16 + 25 - 36)/40 = 5/40 > 0, so C < 90°. Acute.'
      },
      {
        id: 'laws-27',
        question: 'In triangle ABC, A = 50°, a = 10, b = 15. Find angle B.',
        options: ['B ≈ 74.6° or 105.4°', 'B ≈ 50°', 'B ≈ 80°', 'No solution'],
        correctAnswer: 0,
        explanation: 'sin(B) = 15·sin(50°)/10 ≈ 1.149... > 1, so actually no solution. But if sin(B) ≤ 1, two angles possible.'
      },
      {
        id: 'laws-28',
        question: 'In triangle ABC, a = 3, b = 4, C = 90°. Find c using Law of Cosines.',
        options: ['5', '7', '12', '25'],
        correctAnswer: 0,
        explanation: 'c² = 9 + 16 - 2(3)(4)(0) = 25, c = 5.'
      },
      {
        id: 'laws-29',
        question: 'In triangle ABC, A = 120°, b = 5, c = 7. Find side a.',
        options: ['a ≈ 10.44', 'a ≈ 8.66', 'a ≈ 12.00', 'a ≈ 6.00'],
        correctAnswer: 0,
        explanation: 'a² = 25 + 49 - 2(5)(7)cos(120°) = 74 - 70(-0.5) = 109, a ≈ 10.44.'
      },
      {
        id: 'laws-30',
        question: 'Which case always produces a unique triangle?',
        options: ['SSA', 'SSS', 'AAA', 'None'],
        correctAnswer: 1,
        explanation: 'SSS always produces exactly one unique triangle (if triangle inequality is satisfied).'
      }
    ]
  },
  {
    id: 'precalc-9',
    title: 'Complex Numbers & Polar Form',
    unit: 'Complex Numbers',
    description: 'Represent complex numbers in rectangular and polar forms, perform operations, and apply De Moivre\'s Theorem',
    standards: ['CCSS.MATH.CONTENT.HSN.CN.B.4', 'NCTM: Number', 'Ohio: PC.CN.1', 'NY: AI-N.CN.4'],
    // ... (remaining content unchanged)
  },
  {
    id: 'precalc-10',
    title: 'Vectors & Matrices',
    unit: 'Vectors & Linear Algebra',
    description: 'Perform vector operations, model forces, and use matrices to solve systems of equations',
    standards: ['CCSS.MATH.CONTENT.HSN.VM.A.1', 'NCTM: Algebra', 'Ohio: PC.VM.1', 'NY: AI-N.VM.1'],
    // ... (remaining content unchanged)
  }
];